import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, Plus, Share2, Trash2, Clock } from 'lucide-react';
import Canvas from './Canvas';
import StickyNote from './StickyNote';
import useSnapshotShare from '../../hooks/useSnapshotShare';
import useSnapshotHistory from '../../hooks/useSnapshotHistory';
import SnapshotToast from '../../components/SnapshotToast';
import HistoryDrawer from '../../components/HistoryDrawer';
import ConfirmationModal from '../../components/ConfirmationModal';
import ThoughtBubbleIcon from '../../components/ThoughtBubbleIcon';
import { DndContext, closestCenter, TouchSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core';
import { arrayMove, SortableContext, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// --- Sortable Wrapper ---
const SortableStickyNote = ({ id, note, ...props }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    touchAction: 'none',
    position: 'relative', // Ensure it sits in the grid flow
    zIndex: isDragging ? 999 : 'auto',
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners} className="sortable-note-wrapper">
      <StickyNote id={id} {...note} {...props} isMobile={true} />
    </div>
  );
};

const LOCAL_STORAGE_KEY = 'stickyNotes';

function StickyNotesApp() {
  const [notes, setNotes] = useState([]);
  const [activeNoteId, setActiveNoteId] = useState(null);
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [activeDragId, setActiveDragId] = useState(null);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [isClearModalOpen, setIsClearModalOpen] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Sensors for Mobile Drag (Long Press)
  const sensors = useSensors(
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 500,
        tolerance: 5,
      },
    })
  );

  const handleDragStart = (event) => {
    const { active } = event;
    setActiveDragId(active.id);
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setNotes((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
    setActiveDragId(null);
  };

  const activeDragNote = activeDragId ? notes.find(n => n.id === activeDragId) : null;

  // Snapshot History Hook
  const { history, saveSnapshot, restoreSnapshot, deleteSnapshot, clearHistory } = useSnapshotHistory('stickyNotes', notes);

  // Use the shared snapshot hook
  const { handleShare, showToast, setShowToast } = useSnapshotShare(notes, setNotes, 's');

  const handleShareWithSnapshot = () => {
    saveSnapshot('Shared via Link');
    handleShare();
  };

  const handleRestore = (id) => {
    const data = restoreSnapshot(id);
    if (data) {
      setNotes(data);
      setIsHistoryOpen(false);
    }
  };

  // Load notes from localStorage on initial render (if no URL state)
  useEffect(() => {
    // Only load from local storage if URL is empty (hook handles URL)
    const params = new URLSearchParams(window.location.search);
    if (!params.get('s')) {
      try {
        const storedNotes = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (storedNotes) {
          const parsedNotes = JSON.parse(storedNotes);
          if (Array.isArray(parsedNotes)) {
            setNotes(parsedNotes);
          }
        }
      } catch (error) {
        console.error("Error loading notes from localStorage:", error);
      }
    }
  }, []);

  // Save notes to localStorage whenever they change
  useEffect(() => {
    try {
      if (notes.length > 0) {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(notes));
      }
    } catch (error) {
      console.error("Error saving notes:", error);
    }
  }, [notes]);

  const addNote = () => {
    const xOffset = notes.length * 10;
    const yOffset = notes.length * 10;

    const newNote = {
      id: Date.now(),
      text: '',
      color: '#fefc92',
      x: 50 + xOffset,
      y: 50 + yOffset,
    };
    setNotes([...notes, newNote]);
  };

  const updateNotePosition = (id, newX, newY) => {
    setNotes(prevNotes =>
      prevNotes.map(note =>
        note.id === id ? { ...note, x: newX, y: newY } : note
      )
    );
  };

  const updateNoteColor = (id, newColor) => {
    setNotes(prevNotes =>
      prevNotes.map(note =>
        note.id === id ? { ...note, color: newColor } : note
      )
    );
  };

  const updateNoteText = (id, newText) => {
    setNotes(prevNotes =>
      prevNotes.map(note =>
        note.id === id ? { ...note, text: newText } : note
      )
    );
  };

  const deleteNote = (idToDelete) => {
    const updatedNotes = notes.filter(note => note.id !== idToDelete);
    setNotes(updatedNotes);
    // Explicit save to ensure persistence
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updatedNotes));
    } catch (e) {
      console.error("Failed to save after delete", e);
    }
  };

  const clearBoard = () => {
    setIsClearModalOpen(true);
  };

  const confirmClearBoard = () => {
    setNotes([]);
    // Explicit save to ensure persistence
    try {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify([]));
    } catch (e) {
      console.error("Failed to clear storage", e);
    }
    setIsClearModalOpen(false);
  };

  const handleSetActiveNote = (noteId) => {
    setActiveNoteId(noteId);
  };

  return (
    <div className="App">
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 px-4 md:px-6 py-4 flex items-center justify-between sticky top-0 z-10">
        <div
          className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity w-1/3"
          onClick={() => navigate('/')}
        >
          <ThoughtBubbleIcon className="fill-blue-600 text-blue-600 shrink-0" size={24} />
          <span className="text-sm md:text-xl font-bold md:font-extrabold text-blue-600 tracking-tight font-rounded">
            Thoughtblink
          </span>
        </div>

        <div className="flex justify-center w-1/3">
          <h1 className="text-lg md:text-xl font-bold bg-gradient-to-r from-yellow-500 to-orange-600 bg-clip-text text-transparent whitespace-nowrap">
            Sticky Ideas
          </h1>
        </div>

        <div className="flex items-center justify-end gap-4 w-1/3">
          <button
            onClick={handleShareWithSnapshot}
            className="p-2 text-slate-400 hover:text-cyan-600 hover:bg-cyan-50 rounded-full transition-all"
            title="Share Board"
          >
            <Share2 size={20} />
          </button>
          <button
            onClick={() => setIsHistoryOpen(true)}
            className="p-2 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded-full transition-all"
            title="Version History"
          >
            <Clock size={20} />
          </button>
          <button
            onClick={clearBoard}
            className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-all"
            title="Clear Board"
          >
            <Trash2 size={20} />
          </button>
        </div>
      </header>

      {/* Floating Add Button (Always Visible, Top Left) */}
      <button
        onClick={addNote}
        className="fixed bottom-8 right-6 md:top-20 md:left-6 md:bottom-auto md:right-auto z-50 p-4 bg-yellow-400 text-yellow-900 rounded-full shadow-xl hover:bg-yellow-500 active:scale-90 transition-all flex items-center justify-center type-plus-btn scale-on-hover"
        style={{ width: '64px', height: '64px' }}
        aria-label="Add Sticky Note"
      >
        <Plus size={32} strokeWidth={3} />
      </button>

      <Canvas>
        {isMobile ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            {/* Mobile Floating Action Button */}


            <SortableContext items={notes} strategy={rectSortingStrategy}>
              <div className="mobile-notes-grid">
                {notes.map(note => (
                  <SortableStickyNote
                    key={note.id}
                    id={note.id}
                    note={note}
                    onUpdateNotePosition={updateNotePosition}
                    onUpdateNoteText={updateNoteText}
                    onUpdateNoteColor={updateNoteColor}
                    onDeleteNote={deleteNote}
                    isActive={note.id === activeNoteId}
                    onSetActive={handleSetActiveNote}
                  />
                ))}
              </div>
            </SortableContext>
            <DragOverlay>
              {activeDragNote ? (
                <div style={{ transform: 'scale(1.05)', opacity: 1, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)' }}>
                  <StickyNote
                    id={activeDragNote.id}
                    text={activeDragNote.text}
                    color={activeDragNote.color}
                    x={0} y={0}
                    onUpdateNotePosition={() => { }}
                    onUpdateNoteText={() => { }}
                    onUpdateNoteColor={() => { }}
                    onDeleteNote={() => { }}
                    isActive={true}
                    onSetActive={() => { }}
                    isMobile={true}
                    isOverlay={true}
                  />
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        ) : (
          notes.map(note => (
            <StickyNote
              key={note.id}
              id={note.id}
              text={note.text}
              color={note.color}
              x={note.x}
              y={note.y}
              onUpdateNotePosition={updateNotePosition}
              onUpdateNoteText={updateNoteText}
              onUpdateNoteColor={updateNoteColor}
              onDeleteNote={deleteNote}
              isActive={note.id === activeNoteId}
              onSetActive={handleSetActiveNote}
            />
          ))
        )}
      </Canvas>
      <SnapshotToast isVisible={showToast} onClose={() => setShowToast(false)} color="yellow" />
      <HistoryDrawer
        isOpen={isHistoryOpen}
        onClose={() => setIsHistoryOpen(false)}
        history={history}
        onRestore={handleRestore}
        onDelete={deleteSnapshot}
        onClearHistory={clearHistory}
      />
      <ConfirmationModal
        isOpen={isClearModalOpen}
        onClose={() => setIsClearModalOpen(false)}
        onConfirm={confirmClearBoard}
        title="Clear Board?"
        message="Are you sure you want to delete all sticky notes? This action cannot be undone."
        confirmText="Yes, Clear All"
      />
    </div>
  );
}

export default StickyNotesApp;
